/**
 * Clients → Opportunities (prototype VIEWS.opps L1332, openOpp L1337, newOpp L1338, moveOpp L1331, saveOpp L1339).
 *
 * Data: GET /opportunities (every card + the three counts; the server scopes a member to the clients they are SPOC
 * of), GET /opportunities/{id} (the drawer), POST /opportunities ("Add an opportunity" → Spotted),
 * POST /opportunities/{id}/stage ("Move to"; handing to sales may name an active sales person),
 * PATCH /opportunities/{id} (the estimated value — founder and associate director only).
 *
 * Value is revenue: it is rendered only when the API returned `value_lakh` (absent for members), and the value
 * inputs appear only for a caller with `revenue.read`.
 */
import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router';
import { OPP_STAGE_LABEL, OppType, type OppStage } from '@amplispot/contracts';
import { call, newIdempotencyKey } from '../../api';
import { useSession } from '../../lib/session';
import { useClients, useUsers } from '../../lib/data';
import {
  OPP_STAGES,
  oppTypeChip,
  oppValue,
  useOppWrite,
  useOpportunities,
  useOpportunity,
  type Opportunity,
} from '../../lib/opportunities';
import { Avatar, Drawer, Empty, ErrorState, Field, Loading, Modal, errorText, useFail, useToast } from '../../ui/kit';
import { Stat } from '../timesheets/TimesheetReport';

const RO_TITLE = 'Read-only while you view as someone';
const TYPES = OppType.options;

// ================================================================ the tab

export function Opportunities() {
  const { readOnly } = useSession();
  const [params, setParams] = useSearchParams();
  const openId = params.get('opp');
  const [adding, setAdding] = useState(false);
  const q = useOpportunities(null);
  const setOpen = (id: string | null) =>
    setParams(
      (p) => {
        const n = new URLSearchParams(p);
        if (id) n.set('opp', id);
        else n.delete('opp');
        return n;
      },
      { replace: !id },
    );

  return (
    <>
      <div className="ph" style={{ margin: '0 0 12px', justifyContent: 'flex-end' }}>
        <div className="r" style={{ marginLeft: 0 }}>
          <button className="btn pri" onClick={() => setAdding(true)} disabled={readOnly} title={readOnly ? RO_TITLE : undefined}>
            + Add opportunity
          </button>
        </div>
      </div>
      {q.isPending ? (
        <div className="panel">
          <Loading label="Loading opportunities…" />
        </div>
      ) : q.isError ? (
        <div className="panel">
          <ErrorState error={q.error} onRetry={() => q.refetch()} />
        </div>
      ) : (
        <>
          <div className="stats">
            <Stat k="Spotted" v={q.data.stats.total} />
            <Stat k="With sales" v={q.data.stats.with_sales} />
            <Stat k="Closed" v={q.data.stats.closed} cls="green" />
          </div>
          {q.data.data.length ? (
            <Board opps={q.data.data} onOpen={setOpen} />
          ) : (
            <div className="panel">
              <Empty title="No opportunities yet">
                When you notice a client could use more, or knows someone who could, press Add opportunity.
              </Empty>
            </div>
          )}
        </>
      )}
      {adding ? <AddOpportunityModal onClose={() => setAdding(false)} /> : null}
      {openId ? <OpportunityDrawer id={openId} onClose={() => setOpen(null)} /> : null}
    </>
  );
}

// ================================================================ the kanban

function Board({ opps, onOpen }: { opps: Opportunity[]; onOpen: (id: string) => void }) {
  return (
    <div className="kan">
      {OPP_STAGES.map(([k, label]) => {
        const col = opps.filter((o) => o.stage === k);
        return (
          <div className="kcol" key={k} data-stage={k}>
            <div className="kh">
              {label}
              <span className="n">{col.length}</span>
            </div>
            {col.length ? (
              col.map((o) => <Card key={o.id} o={o} onOpen={onOpen} />)
            ) : (
              <div style={{ color: 'var(--mute)', fontSize: 12.5, padding: 8 }}>Empty</div>
            )}
          </div>
        );
      })}
    </div>
  );
}

function Card({ o, onOpen }: { o: Opportunity; onOpen: (id: string) => void }) {
  const value = oppValue(o);
  return (
    <div
      className="kc"
      role="button"
      tabIndex={0}
      onClick={() => onOpen(o.id)}
      onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && (e.preventDefault(), onOpen(o.id))}
    >
      <div className="t">{o.title}</div>
      <div style={{ color: 'var(--sub)', fontSize: 12, marginTop: 3 }}>{o.client.name}</div>
      <div className="m">
        <span className={`chip ${oppTypeChip(o.opp_type)}`}>{o.opp_type}</span>
        {value !== null ? <span className="mono">{value}</span> : null}
        <span style={{ marginLeft: 'auto' }}>
          <Avatar user={o.by} size={26} />
        </span>
      </div>
    </div>
  );
}

// ================================================================ openOpp

function OpportunityDrawer({ id, onClose }: { id: string; onClose: () => void }) {
  const { readOnly, can } = useSession();
  const nav = useNavigate();
  const toast = useToast();
  const q = useOpportunity(id);
  const manager = can('revenue.read') && !readOnly;
  const users = useUsers(can('users.read'));
  const sales = (users.data ?? []).filter((u) => u.role === 'sales' && u.status === 'active');
  const [salesId, setSalesId] = useState('');
  const [editing, setEditing] = useState(false);

  const move = useOppWrite((v: { o: Opportunity; stage: OppStage }) =>
    call('opportunities.move', {
      params: { id: v.o.id },
      ifMatch: v.o.version,
      body: { stage: v.stage, handed_to_id: v.stage === 'handed_to_sales' && salesId ? salesId : undefined },
    }),
  );

  const doMove = (o: Opportunity, stage: OppStage) =>
    move.mutate(
      { o, stage },
      {
        onSuccess: () => {
          toast(`Moved to ${OPP_STAGE_LABEL[stage]}`);
          onClose();
        },
        onError: (e) => toast(errorText(e)),
      },
    );

  const o = q.data;
  const value = o ? oppValue(o) : null;
  const canWrite = !!o && o.can_edit && !readOnly;

  return (
    <Drawer
      title="Opportunity"
      onClose={onClose}
      footer={
        <>
          <div className="grow" />
          <button className="btn" onClick={onClose}>
            Close
          </button>
        </>
      }
    >
      {q.isPending ? (
        <Loading label="Loading the opportunity…" />
      ) : q.isError ? (
        <ErrorState error={q.error} onRetry={() => q.refetch()} />
      ) : o ? (
        <>
          <h2 style={{ fontSize: 18 }}>{o.title}</h2>
          <dl className="kv" style={{ margin: '14px 0' }}>
            <dt>Client</dt>
            <dd>
              <a
                href={`/clients/book?client=${encodeURIComponent(o.client.id)}`}
                onClick={(e) => {
                  e.preventDefault();
                  nav(`/clients/book?client=${encodeURIComponent(o.client.id)}`);
                }}
                style={{ color: 'var(--brand)', fontWeight: 600, textDecoration: 'none' }}
              >
                {o.client.name}
              </a>
              {o.client.priority === 'high' ? (
                <>
                  {' '}
                  <span className="pm" title="High priority client">
                    ▲
                  </span>
                </>
              ) : null}
            </dd>
            <dt>Type</dt>
            <dd>{o.opp_type}</dd>
            {value !== null ? (
              <>
                <dt>Estimated value</dt>
                <dd>
                  {editing ? (
                    <ValueEditor o={o} onDone={() => setEditing(false)} />
                  ) : (
                    <span className="split" style={{ gap: 10 }}>
                      <span>{value}</span>
                      {manager && o.can_edit ? (
                        <button className="btn sm" onClick={() => setEditing(true)}>
                          {o.value_unknown || o.value_lakh === null ? 'Set value' : 'Change'}
                        </button>
                      ) : null}
                    </span>
                  )}
                </dd>
              </>
            ) : null}
            <dt>Spotted by</dt>
            <dd>{o.by.name}</dd>
            {o.handed_to ? (
              <>
                <dt>Handed to</dt>
                <dd>{o.handed_to.name}</dd>
              </>
            ) : null}
          </dl>
          <div className="lbl" style={{ marginBottom: 8 }}>
            Move to
          </div>
          {o.stage !== 'handed_to_sales' && sales.length && canWrite ? (
            <Field label="Sales person, if you are handing it to sales">
              <select className="in" value={salesId} onChange={(e) => setSalesId(e.target.value)}>
                <option value="">Not named yet</option>
                {sales.map((u) => (
                  <option key={u.id} value={u.id}>
                    {u.name}
                  </option>
                ))}
              </select>
            </Field>
          ) : null}
          <div className="split" style={{ flexWrap: 'wrap' }}>
            {OPP_STAGES.filter(([k]) => k !== o.stage).map(([k, label]) => (
              <button
                key={k}
                className="btn sm"
                disabled={!canWrite || move.isPending}
                title={readOnly ? RO_TITLE : !o.can_edit ? 'You cannot change this one' : undefined}
                onClick={() => doMove(o, k)}
              >
                {label}
              </button>
            ))}
          </div>
        </>
      ) : null}
    </Drawer>
  );
}

/** Founder and associate director: the estimated value in ₹ lakh per month, or Unknown. PATCH with If-Match. */
function ValueEditor({ o, onDone }: { o: Opportunity; onDone: () => void }) {
  const { errs, fail, toast } = useFail(['value_lakh']);
  const [text, setText] = useState(o.value_lakh != null ? String(o.value_lakh) : '');
  const save = useOppWrite((body: { value_lakh: number | null; value_unknown?: boolean }) =>
    call('opportunities.update', { params: { id: o.id }, ifMatch: o.version, body }),
  );
  const submit = (unknown: boolean) => {
    let body: { value_lakh: number | null; value_unknown?: boolean };
    if (unknown) body = { value_lakh: null, value_unknown: true };
    else {
      const n = Number(text.replace(/[₹,\s]/g, ''));
      if (!text.trim() || !Number.isFinite(n) || n < 0) return toast('Enter the value in lakh a month, like 1.5');
      if (n > 10_000) return toast('That is more than ₹100 Cr a month. Check the value.');
      body = { value_lakh: n };
    }
    save.mutate(body, {
      onSuccess: () => {
        toast(unknown ? 'Value marked unknown' : 'Value saved');
        onDone();
      },
      onError: fail,
    });
  };
  return (
    <div>
      <div className="split" style={{ gap: 8, flexWrap: 'wrap' }}>
        <span style={{ color: 'var(--sub)' }}>₹</span>
        <input
          className="in"
          inputMode="decimal"
          aria-label="Estimated value, lakh a month"
          style={{ width: 110 }}
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && submit(false)}
          autoFocus
        />
        <span style={{ color: 'var(--sub)' }}>L / mo</span>
        <button className="btn sm pri" onClick={() => submit(false)} disabled={save.isPending}>
          Save
        </button>
        <button className="btn sm" onClick={() => submit(true)} disabled={save.isPending}>
          Unknown
        </button>
        <button className="btn sm" onClick={onDone} disabled={save.isPending}>
          Cancel
        </button>
      </div>
      {errs.value_lakh ? <small className="ferr">{errs.value_lakh}</small> : null}
    </div>
  );
}

// ================================================================ newOpp

const SHOWN = ['title', 'client_id', 'opp_type'] as const;

function AddOpportunityModal({ onClose }: { onClose: () => void }) {
  const clients = useClients();
  const { errs, setErrs, fail, toast } = useFail(SHOWN);
  const [title, setTitle] = useState('');
  const [clientId, setClientId] = useState('');
  const [type, setType] = useState<OppType>('Upsell');
  const [key] = useState(newIdempotencyKey);
  const cid = clientId || clients.data?.[0]?.id || '';

  // Prototype newOpp: no value here — it starts Unknown, and a manager sets it in the drawer (value is founder/AD only).
  const create = useOppWrite(() =>
    call('opportunities.create', { idempotencyKey: key, body: { client_id: cid, title: title.trim(), opp_type: type } }),
  );

  const save = () => {
    setErrs({});
    if (!title.trim()) return toast('Describe it');
    if (!cid) return toast('Pick the client');
    create.mutate(undefined, {
      onSuccess: () => {
        toast('Added to Spotted');
        onClose();
      },
      onError: fail,
    });
  };

  return (
    <Modal
      title="Add an opportunity"
      onClose={onClose}
      footer={
        <>
          <div className="grow" />
          <button className="btn" onClick={onClose}>
            Cancel
          </button>
          <button className="btn pri" onClick={save} disabled={create.isPending || clients.isPending}>
            {create.isPending ? 'Adding…' : 'Add'}
          </button>
        </>
      }
    >
      <Field label="What did you notice" error={errs.title}>
        <input className="in" id="opT" value={title} maxLength={200} onChange={(e) => setTitle(e.target.value)} autoFocus />
      </Field>
      <div className="cols c2">
        <Field label="Client" error={errs.client_id}>
          {clients.isError ? (
            <div className="ferr">{errorText(clients.error)}</div>
          ) : (
            <select className="in" id="opC" value={cid} onChange={(e) => setClientId(e.target.value)} disabled={clients.isPending}>
              {clients.isPending ? <option>Loading…</option> : null}
              {(clients.data ?? []).map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </select>
          )}
        </Field>
        <Field label="Type" error={errs.opp_type}>
          <select className="in" id="opY" value={type} onChange={(e) => setType(e.target.value as OppType)}>
            {TYPES.map((t) => (
              <option key={t}>{t}</option>
            ))}
          </select>
        </Field>
      </div>
    </Modal>
  );
}
